/* @flow */
import React, { Component } from 'react';
import {
  graphql,
  createPaginationContainer,
  type RelayPaginationProp,
} from 'react-relay';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector, initialize, reset } from 'redux-form';
import { TableRow, TableRowColumn } from 'material-ui/Table';
import CreateIcon from 'material-ui/svg-icons/content/create';
import IconButton from 'material-ui/IconButton';
import PhoneIcon from 'material-ui/svg-icons/communication/phone';
import RaisedButton from 'material-ui/RaisedButton';

import Well from '../../components/Well';
import Loader from '../../components/Loader';
import DateTime from '../../components/DateTime';
import UserStatus from '../../components/UserStatus';
import MailTo from '../../components/MailTo';
import Grid from '../../components/Grid';
import TutorsFilter from './components/TutorsFilter';
import enhancer from '../../enhancer';

const PAGE_SIZE = 20;

type Props = {} & RelayPaginationProp;

type State = {
  loading: boolean,
  error: ?Error,
};

let Tutors = class extends Component<Props, State> {
  state = {
    loading: false,
    error: null,
  };

  handleLoadMore = () => {
    this.props.relay.loadMore(PAGE_SIZE, error => this.setState({ error }));
  };

  handleTutorClick = (event, tutorId) => {
    const { history } = this.props;
    history.push(`/admin/tutor/${tutorId}`);
    event.preventDefault();
  };

  handleResetFilter = () => {
    this.props.actions.reset('tutorsForm');
    this.handleFilter();
  };

  handleFilter = () => {
    const { hasStartedJobs, hasOpenJobOffers } = this.props.filterValues;

    this.props.relay.refetchConnection(
      PAGE_SIZE,
      error => {
        this.setState({ error });

        if (error) {
          // eslint-disable-next-line no-alert
          alert(error.message);
        }
      },
      {
        ...this.props.filterValues,
        hasStartedJobs: hasStartedJobs ? true : null,
        hasOpenJobOffers: hasOpenJobOffers ? true : null,
      }
    );

    this.props.actions.initialize('tutorsForm', this.props.filterValues);
  };

  renderRow = ({ node }) => {
    const { email } = node.user;

    return (
      <TableRow key={node.id}>
        <TableRowColumn>
          <MailTo value={email} />
        </TableRowColumn>
        <TableRowColumn>
          <span>
            {node.firstname} {node.lastname}
            <IconButton href={`tel:${node.cellphoneNumber}`}>
              <PhoneIcon />
            </IconButton>
          </span>
        </TableRowColumn>

        <TableRowColumn>{node.openJobOffersCount}</TableRowColumn>
        <TableRowColumn>{node.startedJobsCount}</TableRowColumn>

        <TableRowColumn>
          <UserStatus value={node.user.status} />
        </TableRowColumn>
        <TableRowColumn>
          <DateTime fromNow value={node.createdAt} />
        </TableRowColumn>
        <TableRowColumn>
          <IconButton
            href={`/admin/tutor/${node.id}`}
            onClick={event => {
              this.handleTutorClick(event, node.id);
            }}
          >
            <CreateIcon />
          </IconButton>
        </TableRowColumn>
      </TableRow>
    );
  };

  render() {
    const { loading } = this.state;
    const { allTutors } = this.props.admin;
    const { edges, pageInfo } = allTutors;

    const headers = [
      'Email',
      'Name',
      'Pending Offers',
      'Started Jobs',
      'Status',
      'Created',
      'Actions',
    ];

    return (
      <Well>
        {loading && <Loader />}
        <form>
          <TutorsFilter
            handleFilter={this.handleFilter}
            handleResetFilter={this.handleResetFilter}
            filterValues={this.props.filterValues}
          />

          <Grid headers={headers} renderRow={this.renderRow} rows={edges} />
        </form>

        <RaisedButton
          disabled={!pageInfo.hasNextPage || loading}
          label="Load More"
          secondary
          onClick={this.handleLoadMore}
        />
      </Well>
    );
  }
};

const selector = formValueSelector('tutorsForm');

Tutors = reduxForm({
  form: 'tutorsForm',
})(Tutors);

Tutors = connect(
  state => ({
    filterValues: selector(
      state,
      'name',
      'email',
      'status',
      'hasOpenJobOffers',
      'hasStartedJobs',
      'hasDocuments'
    ),
  }),
  dispatch => ({
    actions: bindActionCreators({ initialize, reset }, dispatch),
  })
)(Tutors);

const query = graphql`
  query TutorsAdminQuery(
    $count: Int!
    $cursor: String
    $email: String
    $hasStartedJobs: Boolean
    $hasOpenJobOffers: Boolean
    $hasDocuments: Boolean
    $status: [UserStatus]
    $name: String
  ) {
    admin {
      ...Tutors_admin @arguments(count: $count, cursor: $cursor)
    }
  }
`;

export default enhancer(Tutors, {
  relay: {
    variables: {
      count: PAGE_SIZE,
    },
    query,
    create: () =>
      createPaginationContainer(
        Tutors,
        {
          admin: graphql`
            fragment Tutors_admin on Admin
              @argumentDefinitions(
                count: { type: "Int" }
                cursor: { type: "String" }
              ) {
              allTutors(
                first: $count
                after: $cursor
                email: $email
                hasStartedJobs: $hasStartedJobs
                hasOpenJobOffers: $hasOpenJobOffers
                hasDocuments: $hasDocuments
                status: $status
                name: $name
              ) @connection(key: "Tutors_allTutors") {
                edges {
                  node {
                    id
                    firstname
                    lastname
                    createdAt
                    openJobOffersCount
                    startedJobsCount
                    cellphoneNumber
                    user {
                      email
                      status
                    }
                  }
                }
                pageInfo {
                  hasNextPage
                }
              }
            }
          `,
        },
        {
          direction: 'forward',
          getConnectionFromProps(props) {
            return props.admin && props.admin.allTutors;
          },
          getFragmentVariables(prevVars, totalCount) {
            return {
              ...prevVars,
              count: totalCount,
            };
          },
          getVariables(props, { count, cursor } /* , fragmentVariables */) {
            return {
              count,
              cursor,
            };
          },
          query,
        }
      ),
  },
});



// WEBPACK FOOTER //
// ./src/modules/admin/tutor/Tutors.js